import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useI18n } from "../i18n";
import { useUi } from "../lib/ui";
import { DEFAULT_PREFS, readConsent, saveConsent, updateConsent } from "../lib/consent";

export default function CookieBanner() {
  const { t, path } = useI18n();
  const { cookiePrefsRequest } = useUi();
  const [visible, setVisible] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [prefs, setPrefs] = useState(DEFAULT_PREFS);

  // First load: re-apply stored consent or show the banner.
  useEffect(() => {
    const stored = readConsent();
    if (stored) {
      setPrefs(stored);
      updateConsent(stored);
      setVisible(false);
    } else {
      setVisible(true);
    }
  }, []);

  // Footer "Cookie preferences" link re-opens the panel.
  useEffect(() => {
    if (cookiePrefsRequest === 0) return;
    const stored = readConsent();
    setPrefs(stored || DEFAULT_PREFS);
    setShowPrefs(true);
    setVisible(true);
  }, [cookiePrefsRequest]);

  const commit = (next) => {
    saveConsent(next);
    setPrefs(next);
    setShowPrefs(false);
    setVisible(false);
  };

  if (!visible) return null;

  const toggle = (key) => setPrefs((p) => ({ ...p, [key]: !p[key] }));

  const categories = [
    { key: "analytics", title: t("cookie.categories.analytics.title"), text: t("cookie.categories.analytics.text") },
    { key: "ads", title: t("cookie.categories.ads.title"), text: t("cookie.categories.ads.text") },
    {
      key: "personalization",
      title: t("cookie.categories.personalization.title"),
      text: t("cookie.categories.personalization.text"),
    },
  ];

  return (
    <div className="cookie-wrap" role="region" aria-label={t("cookie.title")} data-testid="cookie-banner">
      <div className="cookie-card">
        <div className="cookie-main">
          <div className="cookie-copy">
            <h2 className="cookie-title">{t("cookie.title")}</h2>
            <p className="cookie-text">{t("cookie.text")}</p>
            <Link to={path("/cookies")} className="cookie-policy-link" data-testid="cookie-policy-link">
              {t("cookie.policyLink")}
            </Link>
          </div>
          <div className="cookie-actions">
            <button
              type="button"
              className="btn btn-primary btn-compact"
              onClick={() => commit({ necessary: true, analytics: true, ads: true, personalization: true })}
              data-testid="cookie-accept-all"
            >
              {t("cookie.acceptAll")}
            </button>
            <button
              type="button"
              className="btn btn-outline btn-compact"
              onClick={() => commit({ necessary: true, analytics: false, ads: false, personalization: false })}
              data-testid="cookie-reject-all"
            >
              {t("cookie.rejectAll")}
            </button>
            <button
              type="button"
              className="btn btn-ghost btn-compact"
              onClick={() => setShowPrefs((v) => !v)}
              aria-expanded={showPrefs}
              data-testid="cookie-manage"
            >
              {t("cookie.manage")}
            </button>
          </div>
        </div>

        {showPrefs && (
          <div className="cookie-prefs" data-testid="cookie-prefs">
            <h3 className="cookie-prefs-title">{t("cookie.prefsTitle")}</h3>
            <div className="cookie-cat">
              <div className="cookie-cat-copy">
                <strong>{t("cookie.categories.necessary.title")}</strong>
                <span>{t("cookie.categories.necessary.text")}</span>
              </div>
              <span className="cookie-always">{t("cookie.categories.necessary.always")}</span>
            </div>
            {categories.map((c) => (
              <div className="cookie-cat" key={c.key}>
                <div className="cookie-cat-copy">
                  <strong>{c.title}</strong>
                  <span>{c.text}</span>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={Boolean(prefs[c.key])}
                  className={`switch${prefs[c.key] ? " is-on" : ""}`}
                  onClick={() => toggle(c.key)}
                  data-testid={`cookie-toggle-${c.key}`}
                >
                  <span className="switch-knob" />
                </button>
              </div>
            ))}
            <button
              type="button"
              className="btn btn-primary btn-compact cookie-save"
              onClick={() => commit(prefs)}
              data-testid="cookie-save"
            >
              {t("cookie.save")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
